import React, { useEffect, useState } from 'react';
import { CloudSun, CloudLightning, Flame, Snowflake, Loader2, CalendarDays } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const WEATHER_TYPES = [
	{
		key: 'Storm Warning',
		label: 'Storm Warning',
		icon: CloudLightning,
		cardClass: 'storm',
		iconColor: 'text-red-500',
		gradient: 'from-red-500 to-red-300',
        subtitle: 'High audit risk zones requiring immediate attention',
	},
	{
		key: 'Heat Wave',
		label: 'Heat Wave',
		icon: Flame,
		cardClass: 'heat',
		iconColor: 'text-yellow-500',
		gradient: 'from-yellow-500 to-yellow-300',
        subtitle: 'Expiring certificates and credentials',
	},
	{
		key: 'Cold Zone',
		label: 'Cold Zone',
		icon: Snowflake,
		cardClass: 'cold',
		iconColor: 'text-blue-400',
		gradient: 'from-blue-400 to-blue-200',
        subtitle: 'Low-risk areas with stable compliance',
	},
];

const ForecastTab = ({ scanId }) => {
	const [forecastData, setForecastData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [cached, setCached] = useState(false);
	const [lastUpdated, setLastUpdated] = useState(null);
	const [tokensUsed, setTokensUsed] = useState(null);
	const [costUsd, setCostUsd] = useState(null);

	useEffect(() => {
		const fetchCachedForecast = async () => {
			setLoading(true);
			setError(null);
			setCached(false);
			try {
				const cachedResp = await fetch(ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Forecast"), { method: 'POST', credentials: 'include' });
				if (cachedResp.status === 200) {
					const cachedJson = await cachedResp.json();
					if (cachedJson && cachedJson.response) {
						const parsed = JSON.parse(cachedJson.response);
						setForecastData(parsed);
						setCached(true);
						setLastUpdated(cachedJson.lastUpdated || null);
						setTokensUsed(cachedJson.tokens_used || null);
						setCostUsd(cachedJson.cost_usd || null);
						setLoading(false);
						return;
					}
				}
				setForecastData(null);
				setCached(false);
			} catch (err) {
				setForecastData(null);
				setCached(false);
			} finally {
				setLoading(false);
			}
		};
		fetchCachedForecast();
	}, [scanId]);

	const handleGenerate = async () => {
		setLoading(true);
		setError(null);
		setCached(false);
		try {
			const response = await fetch(ApiNames.AI_HUB_FORECAST(scanId), { method: 'POST', credentials: 'include' });
			const json = await response.json();
			const parsed = JSON.parse(json.response);
			setForecastData(parsed);
			setTokensUsed(json.tokens_used || null);
			setCostUsd(json.cost_usd || null);
			setLastUpdated(json.lastUpdated || null);
			setCached(false);
		} catch (err) {
			setError('Unable to generate forecast analysis. Please try again.');
			setForecastData(null);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex flex-col items-center gap-8">
			<div className="w-full max-w-6xl">
				<div className="card-header flex items-center justify-between mb-2">
					<div className="card-title flex items-center gap-2">
						<CloudSun className="w-6 h-6 text-blue-400" />
						<h3 className="font-bold text-lg">AI Compliance Weather Forecast</h3>
					</div>
					<span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
						<CalendarDays className="w-4 h-4 mr-1" />90-Day Outlook
					</span>
				</div>
				<p className="text-gray-400 mb-6 text-sm mt-2">
					AI predicts compliance "weather" based on upcoming deadlines, audit schedules, certificate expirations, and risk trends. The 90-day forecast projects your compliance outlook based on current findings.
				</p>
				<div className="w-full flex flex-col items-center">
					{!cached && !forecastData && !loading && !error && (
						<div className="flex flex-col items-center py-12">
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
								onClick={handleGenerate}
								disabled={loading || !scanId}
							>
								Generate Forecast Analysis
							</button>
						</div>
					)}
					{loading && (
						<div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
							<Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
							<span className="text-gray-400">Generating forecast analysis...</span>
						</div>
					)}
					{error && !loading && (
						<div className="ai-response text-center text-red-400 py-12">{error}</div>
					)}
					{forecastData && !loading && !error && (
						<>
							<div className="weather-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6 w-full">
								{WEATHER_TYPES.map(type => (
									<div key={type.key} className={`weather-card ${type.cardClass} bg-base-100 rounded-xl shadow-xl p-6 flex flex-col items-center animate-fadeIn`}>
										<span className="weather-icon mb-2">
											<type.icon className={`w-8 h-8 ${type.iconColor}`} />
										</span>
										<h4 className="weather-title font-semibold text-base mb-1 text-center">{type.label}</h4>
                                        <p className="weather-subtitle font-semibold text-sm text-gray-600 mb-1 text-center">{type.subtitle}</p>
										<p className="weather-desc text-sm text-gray-500 mt-1" style={{whiteSpace: 'pre-line'}}>{forecastData[type.key]}</p>
									</div>
								))}
							</div>
							<div className="weather-card bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn mb-4 w-full">
								<div className="flex items-center gap-2 mb-2">
									<CalendarDays className="w-5 h-5 text-blue-400" />
									<h4 className="font-bold text-lg">90-Day Compliance Forecast</h4>
								</div>
								<p className="weather-desc text-black-400 mb-4" style={{whiteSpace: 'pre-line'}}>{forecastData['Summary']}</p>
								{tokensUsed && <p className="mb-2 text-gray-400">Tokens used: {tokensUsed}. Cost: ${costUsd}.</p>}
								{cached && lastUpdated && (
									<p className="mb-2 text-gray-400">Viewing cached forecast analysis. Last updated: {lastUpdated}.</p>
								)}
								<button
									className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
									onClick={handleGenerate}
									disabled={loading || !scanId}
								>
									Regenerate Forecast Analysis
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default ForecastTab;
